import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { autonomousOpsAow001Api, type Aow001Status, type SprintDashboard, type SprintValidation } from '../../api/autonomous-ops-aow001';

type Tab = 'overview' | 'modules' | 'task-types' | 'providers' | 'jobs' | 'gates' | 'status';

export function AutonomousOperationsAow001Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [taskTypes, setTaskTypes] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Aow001Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-teal-200 bg-white dark:border-teal-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-teal-700 dark:text-teal-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, types, prov, jb, gt, st] = await Promise.all([
        autonomousOpsAow001Api.dashboard(), autonomousOpsAow001Api.operationsModules(),
        autonomousOpsAow001Api.taskTypes(), autonomousOpsAow001Api.providers(),
        autonomousOpsAow001Api.backgroundJobs(), autonomousOpsAow001Api.qualityGates(),
        autonomousOpsAow001Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setTaskTypes(types); setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await autonomousOpsAow001Api.validate({
        tasks_executable: true, workflow_engine_operational: true,
        approvals_operational: true, automation_rules_operational: true,
        escalations_operational: true, execution_history_complete: true,
        audit_complete: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Autonomous Operations passes all release gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Operations Modules' },
    { id: 'task-types', label: 'Task Types' }, { id: 'providers', label: 'Providers' },
    { id: 'jobs', label: 'Jobs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-teal-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>AOW-000001 · P0 · Mandatory Before Pilot</p>
        <h1 className="text-2xl font-semibold">Autonomous Operations & Workflow Engine</h1>
        <p className={`text-sm ${muted}`}>Convert validated recommendations into completed business outcomes — execution platform, not intelligence.</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/intelligence-qa-iqa001" className={`${accent} hover:underline`}>IQA-000001</Link>
          <span className={muted}>→</span>
          <Link to="/autonomous-ops" className={`${accent} hover:underline`}>Live Execution Command Center</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-teal-300 bg-teal-50 p-3 text-sm text-teal-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-teal-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">From Insight to Measurable Business Outcome</h2>
            <p className={`mt-2 text-sm ${muted}`}>Task engine, workflow orchestration, approval framework, automation rules, escalation engine and execution history — every validated recommendation becomes an executable workflow.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            {[{ label: 'Objectives', value: dashboard.objectives }, { label: 'Operations Modules', value: dashboard.operations_modules },
              { label: 'Task Types', value: dashboard.task_types }, { label: 'Quality Gates', value: dashboard.quality_gates }].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>{m.label}</p><p className="text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-teal-600 px-4 py-2 text-sm text-white disabled:opacity-50">Validate Release Gates</button>
          {validation && <p className={`text-sm ${validation.valid ? 'text-teal-600' : 'text-red-600'}`}>{validation.valid ? 'All gates passed' : validation.gaps?.join(', ')}</p>}
        </div>
      )}

      {tab === 'modules' && modules.map((m) => (
        <div key={m.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{m.name}</p><p className={`text-xs ${muted}`}>{m.category}</p></div>
      ))}

      {tab === 'task-types' && taskTypes.map((t) => (
        <div key={t.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{t.name}</p><p className={`text-xs ${muted}`}>{t.category}</p></div>
      ))}

      {tab === 'providers' && providers.map((p) => (
        <div key={p.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{p.name}</p><p className={`text-xs ${muted}`}>{p.interface}</p></div>
      ))}

      {tab === 'jobs' && jobs.map((j) => (
        <div key={j.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{j.name}</p><p className={`text-xs ${muted}`}>{j.schedule ?? 'on-demand'}</p></div>
      ))}

      {tab === 'gates' && gates.map((g) => (
        <div key={g.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{g.name}</p><p className={`text-xs ${muted}`}>{g.blocking ? 'Blocking' : 'Advisory'}</p></div>
      ))}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">Sprint {status.sprint_id}</p>
          <p className={`text-sm ${muted}`}>{status.objectives} objectives · {status.operations_modules} modules · {status.task_types} task types</p>
          <p className={`text-sm ${accent}`}>Workflow engine · Approval engine · Automation rules · Execution history</p>
        </div>
      )}
    </div>
  );
}
